import { t } from 'i18next';
import { useMutation } from 'react-query';

import { addressService } from 'addresses/services';
import { IRemoveAddressPayload } from 'addresses/types';
import { contactGroupService } from 'contacts/services';
import { IContactGroupAddress, IContactGroupAddressListRequestPayload } from 'contacts/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { IInfiniteQueryOptions } from 'core/types';

export const useGetContactGroupAddresses = (
  requestPayload?: IContactGroupAddressListRequestPayload,
  options: IInfiniteQueryOptions<IContactGroupAddress> = {
    enabled: true,
    fetchAllPages: true,
  }
) =>
  useInfiniteQuery<IContactGroupAddress>(
    contactGroupService.getContactGroupAddresses,
    'contactGroupAddresses',
    requestPayload,
    options
  );

export const useContactGroupActions = (
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => ({
  ...useUpdateContactGroupDefaultPricing(),
  ...useUpdateContactGroup(),
  ...useMergeContactGroups(),
  ...useMergeContactGroupAddresses(),
  ...useUpsertContactGroupAddress(notificationPosition),
  ...useUpsertContactGroup(),
  ...useRemoveContactGroupAddress(notificationPosition),
});

const useUpdateContactGroupDefaultPricing = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactGroupDefaultPricingUpdateSuccess') },
    loading: { message: t('messageContactGroupDefaultPricingUpdateInProgress') },
    error: { title: t('errorTitleContactGroupDefaultPricingUpdate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (data) => contactGroupService.updateContactGroupDefaultPricing(data),
    {
      ...errors,
    }
  );

  return { updateContactGroupDefaultPricing: mutateAsync };
};

const useUpdateContactGroup = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactGroupUpdateSuccess') },
    loading: { message: t('messageContactGroupUpdateInProgress') },
    error: { title: t('errorTitleContactGroupUpdate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => contactGroupService.updateContactGroup(data), {
    ...errors,
  });

  return { updateContactGroup: mutateAsync };
};

const useMergeContactGroups = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactGroupMergeSuccess') },
    loading: { message: t('messageContactGroupMergeInProgress') },
    error: { title: t('errorTitleContactGroupMerge') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => contactGroupService.mergeContactGroups(data), {
    ...errors,
  });

  return { mergeContactGroups: mutateAsync };
};

const useMergeContactGroupAddresses = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactGroupAddressMergeSuccess') },
    loading: { message: t('messageContactGroupAddressMergeInProgress') },
    error: { title: t('errorTitleContactGroupAddressMerge') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (data) => contactGroupService.mergeContactGroupAddresses(data),
    {
      ...errors,
    }
  );

  return { mergeContactGroupAddresses: mutateAsync };
};

const useUpsertContactGroupAddress = (
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    success: ({ address }) =>
      address?.id
        ? { message: t('messageContactGroupAddressUpdateSuccess') }
        : { message: t('messageContactGroupAddressSaveSuccess') },
    loading: ({ address }) =>
      address?.id
        ? { message: t('messageContactGroupAddressUpdateInProgress') }
        : { message: t('messageContactGroupAddressSaveInProgress') },
    error: ({ address }) =>
      address?.id
        ? { title: t('errorTitleContactGroupAddressUpdate') }
        : { title: t('errorTitleContactGroupAddressSave') },
    invalidateQueries: ['contactGroupAddresses'],
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(
    (data) => contactGroupService.upsertContactGroupAddress(data),
    {
      ...errors,
    }
  );

  return { upsertContactGroupAddress: mutateAsync };
};

const useUpsertContactGroup = () => {
  const { errors } = useHandleErrors({
    success: ({ contactGroup }) =>
      contactGroup?.id
        ? { message: t('messageContactGroupUpdateSuccess') }
        : { message: t('messageContactGroupCreateSuccess') },
    loading: ({ contactGroup }) =>
      contactGroup?.id
        ? { message: t('messageContactGroupUpdateInProgress') }
        : { message: t('messageContactGroupCreateInProgress') },
    error: ({ contactGroup }) =>
      contactGroup?.id
        ? { title: t('errorTitleContactGroupUpdate') }
        : { title: t('errorTitleContactGroupCreate') },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation((data) => contactGroupService.upsertContactGroup(data), {
    ...errors,
  });

  return { upsertContactGroup: mutateAsync };
};

const useRemoveContactGroupAddress = (
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactGroupAddressRemoveSuccess') },
    loading: { message: t('messageContactGroupAddressRemoveInProgress') },
    error: { title: t('errorTitleContactGroupAddressRemove') },
    invalidateQueries: ['contactGroupAddresses'],
    position: notificationPosition,
  });

  const { mutateAsync, isLoading } = useMutation(
    (data: IRemoveAddressPayload) => addressService.removeAddress(data),
    {
      ...errors,
    }
  );

  return {
    removeContactGroupAddress: mutateAsync,
    isRemoveContactGroupAddressLoading: isLoading,
  };
};
